import type { EmotionCache } from "@emotion/cache";
import type { AppProps } from "next/app";
import { useLayoutEffect, type ReactNode } from "react";
import dynamic from "next/dynamic";
import { CacheProvider } from "@emotion/react";
import { styled } from "@mui/material";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";

import AlertDialog from "@/components/common/AlertDialog";
import SnackbarHost from "@/components/common/SnackbarHost";
import Analytics from "@/components/common/Analytics";
import ClientErrorCapture from "@/components/common/ClientErrorCapture";
import AuthBootstrap from "@/components/common/AuthBootstrap";
import LiveOrdersBridge from "@/components/common/LiveOrdersBridge";
import OfflineOrdersBridge from "@/components/common/OfflineOrdersBridge";
import ServiceWorkerBridge from "@/components/common/ServiceWorkerBridge";
import AppSkeleton from "@/components/common/AppSkeleton";
import RouteTransition from "@/components/common/RouteTransition";
import BackButtonBridge from "@/components/common/BackButtonBridge";
import ItemOptionsDrawer from "@/components/common/ItemOptionsDrawer";
import { CartDrawerProvider } from "@/context/CartDrawerContext";
import { OffersProvider } from "@/context/OffersContext";
// ADDITION — branch (outlet) selection. Nothing in the recovered bundle
// corresponds to these two; see src/lib/branch.ts for the split between what
// was recovered (hostname resolution) and what was added (the picker).
import { BranchProvider } from "@/context/BranchContext";
import CustomerLoginGate from "@/components/common/CustomerLoginGate";
import SeoHead from "@/components/common/SeoHead";
import { PAGE_TITLES } from "@/constants/routes";
import createEmotionCache from "@/lib/createEmotionCache";
import { persistor, store, useAppDispatch } from "@/store";
import { setSettings } from "@/store/slices/settingsSlice";
import type { StoreSettings } from "@/types";
import ThemeProvider from "@/theme";
// Mapbox ships its own stylesheet and Next only allows a global CSS import
// from here. The delivery-address modal and the tracking page both need it.
import "mapbox-gl/dist/mapbox-gl.css";
import "@/styles/globals.css";

// Closed overlays used to be statically bundled into every route. That made a
// first visit download reports, maps, staff tools and policy UI before the menu
// could become interactive. Keep the item-options drawer eager (it is the
// latency-critical item-click path); split everything else at its actual use.
const lazyOverlay = <T extends object>(loader: () => Promise<T>) =>
  dynamic(loader as never, { ssr: false });
const AboutDrawer = lazyOverlay(() => import("@/components/common/AboutDrawer"));
const AssignOrderTakerModal = lazyOverlay(() => import("@/components/common/AssignOrderTakerModal"));
const ConsumerDetailsDrawer = lazyOverlay(() => import("@/components/common/ConsumerDetailsDrawer"));
const DeliveryAddressModal = lazyOverlay(() => import("@/components/common/DeliveryAddressModal"));
const DeliveryAreasDrawer = lazyOverlay(() => import("@/components/common/DeliveryAreasDrawer"));
const RecentOrderDetailsDrawer = lazyOverlay(() => import("@/components/common/RecentOrderDetailsDrawer"));
const SearchItemsModal = lazyOverlay(() => import("@/components/common/SearchItemsModal"));
const StaffInfoModal = lazyOverlay(() => import("@/components/common/StaffInfoModal"));
const StaffOrdersDrawer = lazyOverlay(() => import("@/components/common/StaffOrdersDrawer"));
const StoreStatusModal = lazyOverlay(() => import("@/components/common/StoreStatusModal"));
const XReportDrawer = lazyOverlay(() => import("@/components/common/XReportDrawer"));
const TableSearchModal = lazyOverlay(() => import("@/components/common/TableSearchModal"));
const DineInGuestsModal = lazyOverlay(() => import("@/components/common/DineInGuestsModal"));
const OffersDrawer = lazyOverlay(() => import("@/components/common/OffersDrawer"));
const BranchPicker = lazyOverlay(() => import("@/components/branch/BranchPicker"));
const OrderInstructionsModal = lazyOverlay(() => import("@/components/common/OrderInstructionsModal"));
const PolicyDrawer = lazyOverlay(() => import("@/components/common/PolicyDrawer"));
const ReviewsDrawer = lazyOverlay(() => import("@/components/common/ReviewsDrawer"));
const SelectTimeModal = lazyOverlay(() => import("@/components/common/SelectTimeModal"));
const VerificationDrawer = lazyOverlay(() => import("@/components/common/VerificationDrawer"));
const StoreTimingsDrawer = lazyOverlay(() => import("@/components/common/StoreTimingsDrawer"));
const ReservationDialog = lazyOverlay(() => import("@/components/common/ReservationDialog"));

const clientSideEmotionCache = createEmotionCache();

/**
 * The original's `_app` wraps everything below the Redux provider in this
 * element — `styled("main")({ position: relative; overflow: hidden; height:
 * 100%; width: 100% })`. It is what stops the menu's two columns from
 * producing a second scrollbar, and it is where the page's `<main>` landmark
 * lives, so no page should declare one of its own.
 */
const Main = styled("main")({
  position: "relative",
  overflow: "hidden",
  height: "100%",
  width: "100%",
});

interface AppPropsWithCache extends AppProps {
  emotionCache?: EmotionCache;
}

/**
 * Replace redux-persist's potentially stale tenant settings before the browser
 * paints the route.  Pages used to dispatch this from a normal effect, so a
 * returning visitor briefly saw the previous branch's colours and imagery and
 * then saw the current branch replace them.  The WebView made that two-paint
 * sequence especially noticeable as a startup blink.
 */
function ServerSettingsBootstrap({
  settings,
  children,
}: {
  settings?: StoreSettings | null;
  children: ReactNode;
}) {
  const dispatch = useAppDispatch();
  useLayoutEffect(() => {
    if (settings !== undefined) dispatch(setSettings(settings));
  }, [dispatch, settings]);
  return children;
}

export default function App({
  Component,
  emotionCache = clientSideEmotionCache,
  pageProps,
  router,
}: AppPropsWithCache) {
  /**
   * **`SeoHead` belongs HERE, not in the pages, and it is deliberately OUTSIDE
   * `PersistGate`.** `AJ` (module 42087):
   *
   * ```js
   * let u = Object.prototype.hasOwnProperty.call(AQ, s.pathname) && a.baseUrl;
   * return jsxs(Provider, { store: oA.h, children: [
   *   u && jsx(SeoHead, { pageName: AQ[s.pathname], ssrSettings: a.data?.settings,
   *                       ssrMetadata: a.metadata, baseUrl: a.baseUrl }),
   *   jsx(Main, { children: jsx(PersistGate, { persistor, loading: null, … }) }),
   * ]});
   * ```
   *
   * `PersistGate loading={null}` renders NOTHING until rehydration, which only
   * happens in the browser — so anything under it contributes nothing to the
   * served HTML. This tree had `SeoHead` inside each page, i.e. under the gate,
   * and the consequence was that **every public route served a head with no
   * title, no canonical, no `og:` tags and no JSON-LD**: a crawler or a link
   * unfurler that does not run JavaScript saw an untitled blank page. Measured
   * against the live original, which serves `next-head-count 15` on `/` and
   * `/menu`. Hoisting it here is what fixes that, and it is where the original
   * puts it.
   *
   * The gate is `hasOwnProperty` — NOT a truthiness test — because `"/"`'s
   * title is deliberately `undefined` (the landing page's title is the business
   * name alone). `baseUrl` is the second half: a route with no
   * `getServerSideProps` has none, and a canonical guessed without one would be
   * wrong.
   *
   * `/review-order` and `/customer-form` are NOT in the map. The original
   * serves them `next-head-count 2` — charset and viewport, nothing else — and
   * their in-page `PrivateSeoHead` is client-only for the same PersistGate
   * reason. Both are checkout steps that should never be indexed.
   */
  const seoTitleKnown = Object.prototype.hasOwnProperty.call(
    PAGE_TITLES,
    router.pathname,
  );
  const baseUrl = (pageProps as { baseUrl?: string }).baseUrl;

  return (
    <CacheProvider value={emotionCache}>
      <Provider store={store}>
        {seoTitleKnown && baseUrl && (
          <SeoHead
            pageName={PAGE_TITLES[router.pathname]}
            ssrSettings={
              (pageProps as { data?: { settings?: never } }).data?.settings
            }
            ssrMetadata={(pageProps as { metadata?: never }).metadata}
            baseUrl={baseUrl}
            seoItem={(pageProps as { seoItem?: never }).seoItem}
          />
        )}
        <Main>
        {/*
          `loading` IS the server-rendered body. It was `null`, so every route
          served an empty <main> and the page only appeared once ~360 kB of JS
          had landed and rehydrated - a long blank, then everything at once.
          A shell renders instead, identically on the server and on the first
          client render, so hydration still matches. See AppSkeleton.
        */}
        <PersistGate loading={<AppSkeleton />} persistor={persistor}>
          {/* Above the theme, as in the original, so the tags load once per
              session rather than once per route. Each is a no-op without its
              env var, and all three are suppressed for staff. */}
          <Analytics />
          {/* ADDITION — client error/warning capture. Renders nothing; installs
              the window.onerror / unhandledrejection / console.error|warn tap
              and batches to POST /client-errors. Mounted here, above the theme,
              so it is armed before any route does. */}
          <ClientErrorCapture />
          {/* Settles who the visitor is and raises
              `common.isAuthCheckComplete`, which gates every authenticated
              fetch. Inside PersistGate so the persisted session is already
              rehydrated when it runs. */}
          <AuthBootstrap />
          {/* The live channel — `GET /web/live/:tenantId`, the same broadcast
              every POS terminal is on. Renders nothing, and does nothing at all
              until a staff session is present. See `lib/liveFeed.ts`. */}
          <LiveOrdersBridge />
          {/* PRODUCT CHANGE §2 — the offline order queue. Reads the queue off
              disk at boot, asks the browser for durable storage, and flushes on
              `online`, on a timer and on demand. Renders nothing, and is here
              rather than on a route because a flush must survive navigation. */}
          <OfflineOrdersBridge />
          {/* Caches the visited app shell, public menu/tables and image assets
              so a returning customer can reopen the ordering flow offline. */}
          <ServiceWorkerBridge />
          <ServerSettingsBootstrap
            settings={(pageProps as { data?: { settings?: StoreSettings | null } }).data?.settings}
          >
          <ThemeProvider>
            {/* The offers provider sits above the tree, as in the original:
                it owns `offersWithStats`, the claimable-offer ids behind the
                wallet chip, and the one redemption-count fetch. */}
            <OffersProvider>
            {/* `sl.Z` — the cart drawer's open state. It sits HERE, directly
                inside the offers provider, because four consumers read it: the
                order bar, the cart drawer itself, the offers drawer and
                `orders/[id]`. */}
            <CartDrawerProvider>
            {/* ADDITION. Sits INSIDE the theme (the picker is a themed modal)
                and inside PersistGate (the branch guard clears the persisted
                basket, so the store must already be rehydrated or the purge
                races the rehydrate and the old branch's lines come back). It
                wraps `Component` because it points the API-fetcher defaults at
                the resolved branch during render, before any child mounts. */}
            <BranchProvider branch={(pageProps as { branch?: never }).branch}>
            {/* ADDITION. Every route here is server-rendered, so a navigation
                is a round trip during which Next keeps the OLD page on screen.
                When that page is empty the operator sees nothing happen. */}
            <RouteTransition />
            <CustomerLoginGate />
            <Component {...pageProps} />
            <StoreTimingsDrawer />
            <ItemOptionsDrawer />
            <OffersDrawer />
            <ReviewsDrawer />
            <PolicyDrawer />
            <AboutDrawer />
            <VerificationDrawer />
            <SelectTimeModal />
            <OrderInstructionsModal />
            <DineInGuestsModal />
            <ConsumerDetailsDrawer />
            <RecentOrderDetailsDrawer />
            <DeliveryAreasDrawer />
            <DeliveryAddressModal />
            <TableSearchModal />
            <SearchItemsModal />
            <StoreStatusModal />
            {/* The staff surfaces. Every one of them renders `null` for a
                visitor who is not signed in as staff — the two drawers are
                closed unless their drawer name is the open one, and both
                modals the same — so mounting them here costs a customer
                nothing and keeps the overlay hosts in one place. */}
            <StaffInfoModal />
            <AssignOrderTakerModal />
            <XReportDrawer />
            <StaffOrdersDrawer />
            <ReservationDialog />
            <AlertDialog />
            <SnackbarHost />
            {/* ADDITION — the branch picker. Renders null unless this brand has
                more than one customer-facing outlet. */}
            <BranchPicker />
            </BranchProvider>
            </CartDrawerProvider>
            </OffersProvider>
            {/* Wires the browser back button to the topmost overlay's back
                control — see `useHashQueryId`. Renders nothing. */}
            <BackButtonBridge />
          </ThemeProvider>
          </ServerSettingsBootstrap>
        </PersistGate>
        </Main>
      </Provider>
    </CacheProvider>
  );
}
